import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Copy, Save } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, type NodeAgentRelease } from "../api";
import { ErrorNotice, LoadingRows, formatTime } from "../components";

const publicEndpoint = "/v1/agents/release";

export function AgentReleasePage({ role }: { role: string }) {
  const client = useQueryClient();
  const [feedback, setFeedback] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState("GET · 无需 API Key");
  const release = useQuery({
    queryKey: ["node-agent-release"],
    queryFn: () => api<NodeAgentRelease>("/admin/v1/node-agent-release"),
  });
  const update = useMutation({
    mutationFn: (body: {
      expected_revision: number;
      image_repository: string;
      image_tag: string;
      reason: string;
    }) => api<NodeAgentRelease>("/admin/v1/node-agent-release", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
    onSuccess: async (data) => {
      setFeedback(`Agent 期望版本已更新为 ${data.image_tag}；后续启动的节点将读取该版本。`);
      await client.invalidateQueries({ queryKey: ["node-agent-release"] });
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!release.data) return;
    setFeedback(null);
    const data = new FormData(event.currentTarget);
    update.mutate({
      expected_revision: release.data.revision,
      image_repository: String(data.get("image_repository")).trim(),
      image_tag: String(data.get("image_tag")).trim(),
      reason: String(data.get("reason")).trim(),
    });
  };
  const copyEndpoint = async () => {
    try {
      await copyText(`${window.location.origin}${publicEndpoint}`);
      setCopyFeedback("已复制");
    } catch {
      setCopyFeedback("复制失败，请手动复制");
    }
    window.setTimeout(() => setCopyFeedback("GET · 无需 API Key"), 1800);
  };

  return <div className="page page--agent-release">
    <header className="page-heading agent-release-heading"><div><p className="eyebrow">NODES / RELEASE</p><h1>Agent 版本</h1><p>指定节点启动时应拉取的容器版本。配置只影响后续启动或执行更新脚本的节点。</p></div></header>
    {(release.error || update.error) && <ErrorNotice error={release.error ?? update.error} />}
    {feedback && <div className="notice notice--positive" role="status"><CheckCircle2 size={17} />{feedback}</div>}
    {release.isPending ? <LoadingRows columns={2} /> : release.data && <>
      <section className="agent-release-current" aria-label="当前 Agent 发布目标">
        <div className="agent-release-current__version">
          <p className="eyebrow">当前期望版本</p>
          <strong>{release.data.image_tag}</strong>
          <span>配置修订 {release.data.revision} · 更新于 {formatTime(release.data.updated_at)}</span>
        </div>
        <div className="agent-release-current__image">
          <span>完整镜像</span>
          <code>{release.data.image}</code>
        </div>
      </section>

      <div className="agent-release-layout">
        <section className="agent-release-editor">
          <div className="agent-release-section-copy"><p className="eyebrow">发布设置</p><h2>节点启动目标</h2><p>请先完成镜像推送，再修改这里的版本。保存操作不会立即重启正在处理任务的节点。</p></div>
          {role === "ADMIN" ? <form className="agent-release-form" key={release.data.revision} onSubmit={submit}>
            <label className="field">Agent 镜像仓库<input name="image_repository" defaultValue={release.data.image_repository} required minLength={3} maxLength={512} spellCheck={false} /><span className="field__hint">不含标签，例如 ccr.ccs.tencentyun.com/dudutalk/webrtc-ns-node-agent。</span></label>
            <label className="field">期望版本标签<input name="image_tag" defaultValue={release.data.image_tag} required minLength={1} maxLength={128} spellCheck={false} /><span className="field__hint">必须与已经推送到镜像仓库的标签完全一致。</span></label>
            <label className="field">变更原因<textarea name="reason" required minLength={3} maxLength={500} placeholder="例如：发布节点自动命名与扩容支持" /></label>
            <button className="button button--primary" type="submit" disabled={update.isPending}><Save size={16} />{update.isPending ? "正在保存…" : "保存期望版本"}</button>
          </form> : <p className="transcode-settings__readonly">当前账号只有查看权限。</p>}
        </section>

        <aside className="agent-release-api">
          <p className="eyebrow">PUBLIC BOOTSTRAP API</p>
          <h2>启动配置接口</h2>
          <p>节点启动脚本可以免认证读取。响应只包含公开的镜像地址、标签与配置修订号。</p>
          <div className="agent-release-api__endpoint"><code>{publicEndpoint}</code><button type="button" className="icon-button" title="复制完整接口地址" aria-label="复制完整接口地址" onClick={() => void copyEndpoint()}><Copy size={16} /></button></div>
          <pre><code>{`curl -fsSL \
  "${window.location.origin}${publicEndpoint}"`}</code></pre>
          <span className="agent-release-api__feedback" aria-live="polite">{copyFeedback}</span>
        </aside>
      </div>
    </>}
  </div>;
}

async function copyText(value: string): Promise<void> {
  if (typeof navigator.clipboard?.writeText === "function") {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement("textarea");
  input.value = value;
  input.setAttribute("readonly", "");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  const copied = document.execCommand("copy");
  input.remove();
  if (!copied) throw new Error("clipboard unavailable");
}
